export function formatPhone(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function formatDate(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatTime(value) {
  if (!value) return "";
  const [hours, minutes] = String(value).split(":");
  const hour = Number(hours);
  if (Number.isNaN(hour)) return value;
  const suffix = hour >= 12 ? "PM" : "AM";
  const twelve = hour % 12 || 12;
  return `${twelve}:${minutes || "00"} ${suffix}`;
}

export function formatCurrency(value) {
  const amount = Number(value);
  if (Number.isNaN(amount)) return "";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function statusLabel(status) {
  const map = {
    active: "Active",
    searching: "Searching",
    under_contract: "Under contract",
    closed: "Closed",
    scheduled: "Scheduled",
    completed: "Completed",
    cancelled: "Cancelled",
    pending: "Pending",
    escrow: "Escrow",
    inspection: "Inspection",
    appraisal: "Appraisal",
    custom: "Custom",
    dissolved: "Dissolved",
    open: "Open",
    abandoned: "Abandoned",
    not_started: "Not started",
    in_progress: "In progress",
  };
  return map[status] || status;
}

export const BUYER_STEPS = [
  { path: "/start", label: "Start", key: "start" },
  { path: "/location", label: "Location", key: "location" },
  { path: "/home-type", label: "Home type", key: "homeType" },
  { path: "/lot", label: "Lot", key: "lot" },
  { path: "/exterior", label: "Exterior", key: "exterior" },
  { path: "/interior", label: "Interior", key: "interior" },
  { path: "/lifestyle", label: "Lifestyle", key: "lifestyle" },
  { path: "/accessibility", label: "Access", key: "accessibility" },
  { path: "/notes", label: "Notes", key: "notes" },
  { path: "/tips", label: "Tips", key: "tips" },
  { path: "/transaction", label: "Transaction", key: "transaction" },
  { path: "/complete", label: "Complete", key: "complete" },
];

export const BUYER_SIDEBAR_KEYS = ["notes", "tips", "transaction"];

export const BUYER_FRAME_STEPS = BUYER_STEPS.filter((step) => !BUYER_SIDEBAR_KEYS.includes(step.key));

export function stepIndex(pathname) {
  const frame = BUYER_FRAME_STEPS.findIndex((step) => step.path === pathname);
  if (frame >= 0) return frame;
  return BUYER_STEPS.findIndex((step) => step.path === pathname);
}

export function nextStepPath(pathname) {
  const index = BUYER_FRAME_STEPS.findIndex((step) => step.path === pathname);
  if (index < 0) return "/complete";
  return BUYER_FRAME_STEPS[Math.min(index + 1, BUYER_FRAME_STEPS.length - 1)]?.path || "/complete";
}

export function prevStepPath(pathname) {
  const index = BUYER_FRAME_STEPS.findIndex((step) => step.path === pathname);
  if (index < 0) return "/start";
  return BUYER_FRAME_STEPS[Math.max(index - 1, 0)]?.path || "/start";
}

export function preferenceProgress(preferences = {}) {
  const keys = [
    "location",
    "homeType",
    "lot",
    "exterior",
    "interior",
    "lifestyle",
    "accessibility",
    "notes",
    "tipsAcknowledged",
    "transactionChecklist",
  ];
  const done = keys.filter((key) => hasContent(preferences[key])).length;
  return {
    done,
    total: keys.length,
    percent: Math.round((done / keys.length) * 100),
  };
}

export function hasContent(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return true;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") {
    return Object.values(value).some((item) => hasContent(item));
  }
  return false;
}

export function prettyValue(value) {
  if (value == null || value === "") return "";
  if (Array.isArray(value)) return value.map(prettyValue).filter(Boolean).join(", ");
  if (typeof value === "number") return String(value);
  return String(value)
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatNotesText(notes) {
  if (!notes) return "";
  if (typeof notes === "string") return notes;
  return [
    notes.dealBreakers && `Deal-breakers: ${notes.dealBreakers}`,
    notes.mustHaves && `Must-haves: ${notes.mustHaves}`,
    notes.householdNeeds && `Household needs: ${notes.householdNeeds}`,
    notes.priorities && `Priorities: ${notes.priorities}`,
    notes.specialNotes && notes.specialNotes,
  ]
    .filter(Boolean)
    .join("\n\n");
}

function line(label, value, suffix = "") {
  if (!hasContent(value)) return null;
  const display = typeof value === "number" ? `${value}${suffix}` : prettyValue(value);
  return `${label}: ${display}${typeof value === "number" ? "" : suffix}`;
}

export function summarizeChecklist(preferences = {}) {
  const loc = preferences.location || {};
  const home = preferences.homeType || {};
  const lot = preferences.lot || {};
  const exterior = preferences.exterior || {};
  const interior = preferences.interior || {};
  const lifestyle = preferences.lifestyle || {};
  const access = preferences.accessibility || {};
  const notes = preferences.notes;
  const txn = preferences.transactionChecklist || {};

  const sections = [
    {
      title: "Location",
      lines: [
        line("Region", loc.region),
        line("State", loc.state),
        line("County", loc.county),
        line("City", loc.city),
        line("Setting", loc.setting),
        line("Commute mode", loc.commuteMode),
        line("Max commute", loc.commuteMinutes, " min"),
        line("Climate", loc.climate),
        line("Noise", loc.noise),
        loc.proximityWork != null && `Work proximity: ${loc.proximityWork} min`,
        loc.proximityFamily != null && `Family proximity: ${loc.proximityFamily} min`,
        loc.proximityAirport != null && `Airport proximity: ${loc.proximityAirport} min`,
        loc.proximityShopping != null && `Shopping proximity: ${loc.proximityShopping} min`,
        loc.proximityRecreation != null && `Recreation proximity: ${loc.proximityRecreation} min`,
        loc.notes && `Notes: ${loc.notes}`,
      ].filter(Boolean),
    },
    {
      title: "Home type",
      lines: [
        line("Types", home.homeTypes || home.type),
        line("Age", home.age),
        line("Floor plan", home.floorplan),
        home.sqftMin && `Living area: ${home.sqftMin}–${home.sqftMax || "open"} sq ft`,
        line("Lot size", home.lotSize),
        line("Terrain", home.terrain),
        home.notes && `Notes: ${home.notes}`,
      ].filter(Boolean),
    },
    {
      title: "Lot",
      lines: [
        line("Lot type", lot.lotType),
        line("Fencing", lot.fencing),
        line("Yard size", lot.yardSize),
        line("Landscaping", lot.landscaping),
        line("Driveway", lot.driveway),
        line("RV parking", lot.rvParking),
        line("Garage", lot.garage),
        line("HOA", lot.hoa),
        line("Terrain details", lot.terrainDetails),
        lot.notes && `Notes: ${lot.notes}`,
      ].filter(Boolean),
    },
    {
      title: "Exterior",
      lines: [
        line("Porch", exterior.porch),
        line("Deck", exterior.deck),
        line("Patio", exterior.patio),
        line("Outdoor kitchen", exterior.outdoorKitchen),
        line("Pool / spa", exterior.poolSpa),
        line("Solar", exterior.solar),
        line("Roof", exterior.roof),
        line("Siding", exterior.siding),
        line("Windows", exterior.windows),
        line("Access features", exterior.accessibility),
        exterior.notes && `Notes: ${exterior.notes}`,
      ].filter(Boolean),
    },
    {
      title: "Interior",
      lines: [
        line("Ceiling height", interior.ceilingHeight),
        line("Ceiling types", interior.ceilingTypes),
        interior.ceilingNotes && `Ceiling notes: ${interior.ceilingNotes}`,
        line("Wall types", interior.wallTypes),
        interior.wallNotes && `Wall notes: ${interior.wallNotes}`,
        line("Flooring", interior.flooring),
        interior.flooringNotes && `Flooring notes: ${interior.flooringNotes}`,
        line("Architectural features", interior.architectural),
        interior.architecturalNotes && `Architectural notes: ${interior.architecturalNotes}`,
        line("Kitchen size", interior.kitchenSize),
        line("Primary kitchen layout", interior.kitchenPrimaryLayout),
        line("Kitchen layout", interior.kitchenLayout),
        line("Kitchen details", interior.kitchenDetails || interior.appliances),
        line("Countertops", interior.countertops),
        line("Cabinets", interior.cabinets),
        interior.kitchenNotes && `Kitchen notes: ${interior.kitchenNotes}`,
        line("Living room", interior.livingRoom),
        line("Fireplace", interior.fireplace),
        line("Natural light", interior.naturalLight),
        interior.bedrooms != null && `Bedrooms: ${interior.bedrooms}+`,
        line("Primary suite", interior.masterSuite),
        line("Closets", interior.closets),
        line("Bathrooms", interior.bathroomDetails || interior.bathrooms),
        interior.bathNotes && `Bathroom notes: ${interior.bathNotes}`,
        line("Bonus rooms", interior.bonusRooms),
        interior.bonusNotes && `Bonus-room notes: ${interior.bonusNotes}`,
        interior.notes && `Notes: ${interior.notes}`,
      ].filter(Boolean),
    },
    {
      title: "Lifestyle",
      lines: [
        line("Pets", lifestyle.pets),
        line("Entertainment", lifestyle.entertainment),
        line("Quiet spaces", lifestyle.quietSpaces),
        line("Smart home", lifestyle.smartHome),
        line("Energy", lifestyle.energy),
        line("Maintenance", lifestyle.maintenance),
        line("Other needs", lifestyle.specificNeeds),
        lifestyle.notes && `Notes: ${lifestyle.notes}`,
      ].filter(Boolean),
    },
    {
      title: "Accessibility",
      lines: [
        line("No-step entry", access.noStepEntry),
        line("Wide hallways", access.wideHallways),
        line("Walk-in shower", access.walkInShower),
        line("Ramp access", access.rampAccess),
        line("ADA kitchen", access.adaKitchen),
        line("ADA bathroom", access.adaBathroom),
        line("Other requirements", access.mobilityNeeds),
        access.notes && `Notes: ${access.notes}`,
      ].filter(Boolean),
    },
    {
      title: "Special notes",
      lines: formatNotesText(notes)
        ? formatNotesText(notes).split("\n\n")
        : [],
    },
    {
      title: "Transaction",
      lines: Object.entries(txn.statuses || {})
        .filter(([, status]) => status)
        .map(([id, status]) => `${prettyValue(id)}: ${prettyValue(status)}`),
    },
  ];

  return sections.filter((section) => section.lines.length);
}
